<template>
  <div class="pagination-container">
    <div class="pagination-slot-top" v-if="isTopSlot">
      <slot name="topSlot"></slot>
    </div>

    <div
      class="pagination-layout"
      :style="{
        'padding-bottom': isLeftSlot ? 0.75 + 'rem' : 0,
      }"
    >
      <TirPmPaginationShowMoreElements
        v-if="showMoreElements"
        class="show-more-elements"
        :options="options"
        v-model:countElements="innerCountElements"
      />
      <div class="pagination-slot-left" v-if="isLeftSlot">
        <slot name="leftSlot"> </slot>
      </div>
      <ul class="pagination" v-if="!props.isSmall">
        <li
          class="pagination__control pagination-control"
          :class="{ 'pagination-control--active': isPrevcontrolsActive }"
          @click="goToPrev"
        >
          <ChevronLeftIcon></ChevronLeftIcon>
        </li>
        <TirPmPaginationItem
          v-for="page in pagination"
          :key="`pagination-page-${page}`"
          :page="page"
          :current="modelValue"
          @update="updatePageHandler"
        />
        <li
          class="pagination__control pagination-control"
          :class="{ 'pagination-control--active': isNextcontrolsActive }"
          @click="goToNext"
        >
          <ChevronRightIcon></ChevronRightIcon>
        </li>
      </ul>
      <div class="pagination-small" v-if="props.isSmall">
        <div
          class="pagination__control pagination-control"
          :class="{ 'pagination-control--active': isPrevcontrolsActive }"
          @click="goToPrev"
        >
          <ChevronLeftIcon></ChevronLeftIcon>
        </div>
        {{ modelValue }} из {{ pagination.at(-1) }} страниц
        <div
          class="pagination-control"
          :class="{ 'pagination-control--active': isNextcontrolsActive }"
          @click="goToNext"
        >
          <ChevronRightIcon></ChevronRightIcon>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PropType, computed, defineEmits, defineProps, useSlots } from "vue";
import TirPmPaginationItem from "./tir-pm-pagination-item.vue";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "tir-style-system/icons/solid";
import TirPmPaginationShowMoreElements from "./tir-pm-pagination-show-more-elements.vue";
import { ITirPmDropdownOption } from "tir-components";

// import IconPageFirst from "../assets/icons/page-first.svg";
// import IconPageLast from "../assets/icons/page-last.svg";
// import IconChevronLeft from "../assets/icons/chevron-left.svg";
// import IconChevronRight from "../assets/icons/chevron-right.svg";

const props = defineProps({
  countElements: {type: Number as PropType<number>},
  /**
   * Отображение блока для указания кол-ва элементов
   */
  showMoreElements: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Опции для showMoreElements
   */
  options: { type: Array as PropType<number[]> },
  /**
   * Общее количество страниц
   */
  pages: {
    type: Number as PropType<number>,
    default: 0,
  },
  /**
   * Разбег между страницами
   */
  rangeSize: {
    type: Number as PropType<number>,
    default: 1,
  },
  /**
   * Текущее значение страницы
   */
  modelValue: {
    type: Number as PropType<number>,
    default: 0,
  },
  /**
   * Скрывать первую кнопку
   */
  isHideFirstButton: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Скрывать последнюю кнопку
   */
  isHideLastButton: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Минимальная пагинация без отображения кнопок соседних страниц
   */
  isSmall: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
});

const emit = defineEmits<{
  (eventName: "update:modelValue", value: number): void;
  (e: "update:countElements", count: number): void;
}>();

const slots = useSlots();

/**
 * Сверху для кнопки
 */
const isTopSlot = computed(() => slots && slots.topSlot && slots.topSlot());
/**
 * Слот для прочего
 */
const isLeftSlot = computed(() => slots && slots.leftSlot && slots.leftSlot());

const innerCountElements = computed({
  get: () => props.countElements,
  set: (count: number) => {
    emit("update:countElements", count);
  },
});

// pagination
const pagination = computed((): (number | null)[] => {
  const res = [];
  const minpaginationElems = 5 + props.rangeSize * 2;

  let rangeStart =
    props.pages <= minpaginationElems ? 1 : props.modelValue - props.rangeSize;
  let rangeEnd =
    props.pages <= minpaginationElems
      ? props.pages
      : props.modelValue + props.rangeSize;

  rangeEnd = rangeEnd > props.pages ? props.pages : rangeEnd;
  rangeStart = rangeStart < 1 ? 1 : rangeStart;

  if (props.pages > minpaginationElems) {
    const isStartBoundaryReached = rangeStart - 1 < 3;
    const isEndBoundaryReached = props.pages - rangeEnd < 3;

    if (isStartBoundaryReached) {
      rangeEnd = minpaginationElems - 2;
      for (let i = 1; i < rangeStart; i++) {
        res.push(i);
      }
    } else {
      res.push(1);
      res.push(null);
    }

    if (isEndBoundaryReached) {
      rangeStart = props.pages - (minpaginationElems - 3);
      for (let i = rangeStart; i <= props.pages; i++) {
        res.push(i);
      }
    } else {
      for (let i = rangeStart; i <= rangeEnd; i++) {
        res.push(i);
      }
      res.push(null);
      res.push(props.pages);
    }
  } else {
    for (let i = rangeStart; i <= rangeEnd; i++) {
      res.push(i);
    }
  }

  return res;
});

function updatePageHandler(params: number) {
  emit("update:modelValue", params);
}

// controls
const isPrevcontrolsActive = computed((): boolean => {
  return props.modelValue > 1;
});
const isNextcontrolsActive = computed((): boolean => {
  return props.modelValue < props.pages;
});

// function goToFirst(): void {
//   if (isPrevcontrolsActive.value) {
//     emit("update:modelValue", 1);
//   }
// }
function goToPrev(): void {
  if (isPrevcontrolsActive.value) {
    emit("update:modelValue", props.modelValue - 1);
  }
}

// function goToLast(): void {
//   if (isNextcontrolsActive.value) {
//     emit("update:modelValue", props.pages);
//   }
// }
function goToNext(): void {
  if (isNextcontrolsActive.value) {
    emit("update:modelValue", props.modelValue + 1);
  }
}
</script>

<style lang="scss">
@import "./style.scss";
</style>
